import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Input, Alert, message, Row, Col,
  Divider, Avatar, Descriptions, Tag,
  Button,
  Select
} from 'antd';
import {
  UserOutlined, MailOutlined, LockOutlined,
  CalendarOutlined, EditOutlined, CheckCircleOutlined,
  CloseCircleOutlined, InfoCircleOutlined,
  PlusOutlined
} from '@ant-design/icons';
import axios from '../../utils/axios';
import ReusableTable from '../../components/ReusableTable';
import { Option } from 'antd/es/mentions';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    status: '',
    role: ''
  });

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Récupérer les utilisateurs
  const fetchUsers = async (page = 1, size = 10) => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users/all', {
        params: {
          page,
          limit: size,
          search: searchText,
          status: filters.status,
          role: filters.role
        }
      });
     
      if (response.data.success) {
        const usersData = response.data.users || [];
        const formattedUsers = usersData.map(user => ({
          ...user,
          id: user._id || user.id,
          formattedCreatedAt: formatDate(user.createdAt),
          formattedUpdatedAt: formatDate(user.updatedAt),
          status: user.isActive ? 'Actif' : 'Inactif',
          role: user.role || 'User'
        }));
        setUsers(formattedUsers);
        setFilteredUsers(formattedUsers);
      } else {
        message.error(response.data.message || 'Erreur lors du chargement');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage, pageSize);
  }, [currentPage, pageSize]);

  // Appliquer les filtres
  const applyFilters = () => {
    fetchUsers(1, pageSize);
  };

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Handle search
  const handleSearch = () => {
    fetchUsers(1, pageSize);
  };

  // Handle submit
  const handleSubmit = async (values) => {
    setSubmitting(true);
    setErrorMessage('');
   
    try {
      if (editingUser) {
        values.id = editingUser._id;
        const response = await axios.put('/api/users/edit', values);
       
        if (response.data.success) {
          message.success('Utilisateur modifié avec succès');
          setModalVisible(false);
          setEditingUser(null);
          form.resetFields();
          fetchUsers(currentPage, pageSize);
        } else {
          setErrorMessage(response.data.message || 'Erreur lors de la modification');
        }
      } else {
        const response = await axios.post('/api/users/add', values);
       
        if (response.data.success) {
          message.success('Utilisateur ajouté avec succès');
          setModalVisible(false);
          form.resetFields();
          fetchUsers(currentPage, pageSize);
        } else {
          setErrorMessage(response.data.message || 'Erreur lors de la création');
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrorMessage(error.response?.data?.message || 'Une erreur est survenue');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (user) => {
    Modal.confirm({
      title: 'Confirmer la suppression',
      content: `Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.name} ?`,
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const response = await axios.delete('/api/users/delete', {
            data: { id: user._id }
          });
          if (response.data.success) {
            message.success('Utilisateur supprimé avec succès');
            fetchUsers(currentPage, pageSize);
          } else {
            message.error(response.data.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('Error deleting user:', error);
          message.error(error.response?.data?.message || 'Erreur lors de la suppression');
        }
      }
    });
  };

  // Handle status change
  const handleStatusChange = async (user) => {
    const newStatus = !user.isActive;
    try {
      const response = await axios.patch(`/api/users/${user._id}/status`, {
        isActive: newStatus
      });
      if (response.data.success) {
        message.success(`Statut changé à "${newStatus ? 'Actif' : 'Inactif'}" avec succès`);
        fetchUsers(currentPage, pageSize);
      } else {
        message.error(response.data.message || 'Erreur lors du changement de statut');
      }
    } catch (error) {
      console.error('Error changing status:', error);
      message.error('Erreur lors du changement de statut');
    }
  };

  // Show modal
  const showModal = (user = null) => {
    setEditingUser(user);
    setErrorMessage('');
   
    if (user) {
      form.setFieldsValue({
        name: user.name,
        email: user.email,
        role: user.role || 'User'
      });
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  // Show detail modal
  const showDetailModal = (user) => {
    setSelectedUser(user);
    setDetailModalVisible(true);
  };

  // Close modals
  const handleCancel = () => {
    setModalVisible(false);
    setDetailModalVisible(false);
    setEditingUser(null);
    setSelectedUser(null);
    setErrorMessage('');
    form.resetFields();
  };

  // Validation
  const validatePassword = (_, value) => {
    if (!value && !editingUser) {
      return Promise.reject(new Error('Veuillez saisir le mot de passe'));
    }
    if (value && value.length < 6) {
      return Promise.reject(new Error('Minimum 6 caractères'));
    }
    return Promise.resolve();
  };

  const validateConfirmPassword = ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value && !editingUser) {
        return Promise.reject(new Error('Veuillez confirmer le mot de passe'));
      }
      if (value && value !== getFieldValue('password')) {
        return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
      }
      return Promise.resolve();
    },
  });

  // Options de statut
  const statusOptions = [
    {
      value: 'Actif',
      label: 'Actif',
      color: '#52c41a',
      backgroundColor: '#f6ffed',
      borderColor: '#b7eb8f',
      icon: <CheckCircleOutlined />
    },
    {
      value: 'Inactif',
      label: 'Inactif',
      color: '#f5222d',
      backgroundColor: '#fff1f0',
      borderColor: '#ffa39e',
      icon: <CloseCircleOutlined />
    }
  ];

  // Calculer les statistiques
  const stats = {
    total: filteredUsers.length,
    actif: filteredUsers.filter(u => u.status === 'Actif').length,
    inactif: filteredUsers.filter(u => u.status === 'Inactif').length,
    admin: filteredUsers.filter(u => u.role === 'Admin').length
  };

  // Données paginées
  const paginatedData = filteredUsers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Configuration des colonnes
  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text) => <span style={{ fontWeight: '500' }}>{text}</span>,
      cellStyle: { fontWeight: '500' }
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MailOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
          <span style={{ color: '#1890ff' }}>{email}</span>
        </div>
      )
    },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      render: (role) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: role === 'Admin' ? '#f0f5ff' : '#f6ffed',
          color: role === 'Admin' ? '#2f54eb' : '#52c41a'
        }}>
          {role}
        </span>
      )
    },
    {
      title: 'Créé le',
      dataIndex: 'formattedCreatedAt',
      key: 'createdAt',
      width: 160,
      render: (date) => (
        <div style={{
          fontSize: '12px',
          color: '#666',
          fontFamily: 'monospace'
        }}>
          {date}
        </div>
      )
    },
    {
      title: 'Mis à jour',
      dataIndex: 'formattedUpdatedAt',
      key: 'updatedAt',
      width: 160,
      render: (date) => (
        <div style={{
          fontSize: '12px',
          color: '#666',
          fontFamily: 'monospace'
        }}>
          {date}
        </div>
      )
    }
  ];

  // Configuration des filtres
  const tableFilters = [
    {
      field: 'status',
      placeholder: 'Statut',
      value: filters.status,
      options: [
        { value: 'Actif', label: 'Actif' },
        { value: 'Inactif', label: 'Inactif' }
      ],
      width: '120px'
    },
    {
      field: 'role',
      placeholder: 'Rôle',
      value: filters.role,
      options: [
        { value: 'Admin', label: 'Admin' },
        { value: 'User', label: 'User' },
        { value: 'Moderator', label: 'Modérateur' }
      ],
      width: '140px'
    }
  ];

  // Custom actions
  const customActions = [
    {
      tooltip: 'Changer statut',
      icon: <CheckCircleOutlined />,
      onClick: handleStatusChange,
      color: '#fa8c16'
    }
  ];

  return (
    <>
      <ReusableTable
        // Configuration de base
        title="Gestion des Utilisateurs"
        data={paginatedData}
        columns={columns}
        loading={loading}
       
        // Pagination
        totalItems={filteredUsers.length}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={(page, size) => {
          setCurrentPage(page);
          if (size) setPageSize(size);
        }}
        onSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
       
        // Recherche et filtres
        searchText={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        filters={tableFilters}
        onFilterChange={handleFilterChange}
       
        // Actions
        onAdd={() => showModal()}
        onRefresh={() => fetchUsers(currentPage, pageSize)}
        onEdit={showModal}
        onDelete={handleDelete}
        onView={showDetailModal}
       
        // Personnalisation
        addButtonText="Nouvel Utilisateur"
        searchPlaceholder="Rechercher utilisateur..."
        showStats={true}
        stats={stats}
       
        // Colonnes spéciales
        showStatusColumn={true}
        statusOptions={statusOptions}
       
        // Configuration des boutons d'action
        actionButtons={['view', 'edit', 'delete']}
        customActions={customActions}
       
        // Personnalisation des couleurs
        primaryColor="#5fa5e7"
        borderColor="#5fa5e7"
       
        // Options supplémentaires
        extraButtons={
          <Button
            type="default"
            onClick={() => {
              setSearchText('');
              setFilters({ status: '', role: '' });
              fetchUsers(1, pageSize);
              message.info('Filtres réinitialisés');
            }}
            style={{
              borderColor: '#ff4d4f',
              color: '#ff4d4f'
            }}
          >
            Réinitialiser
          </Button>
        }
       
        // Contenu personnalisé
        headerContent={
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e8e8e8'
            }}>
              <div>
                <h1 style={{
                  margin: '0 0 8px 0',
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <UserOutlined style={{ color: '#5fa5e7' }} />
                  Gestion des Utilisateurs
                </h1>
                <div style={{
                  fontSize: '14px',
                  color: '#666',
                  display: 'flex',
                  gap: '24px',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <span><strong>{stats.actif}</strong> Actif{stats.actif > 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CloseCircleOutlined style={{ color: '#f5222d' }} />
                    <span><strong>{stats.inactif}</strong> Inactif{stats.inactif > 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UserOutlined style={{ color: '#2f54eb' }} />
                    <span><strong>{stats.admin}</strong> Admin{stats.admin > 1 ? 's' : ''}</span>
                  </div>
                  <div style={{
                    backgroundColor: '#5fa5e7',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontWeight: '500'
                  }}>
                    Total: {stats.total}
                  </div>
                </div>
              </div>
             
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showModal()}
                size="large"
                style={{
                  backgroundColor: '#5fa5e7',
                  borderColor: '#5fa5e7',
                  padding: '0 24px',
                  height: '40px',
                  fontWeight: '500',
                  borderRadius: '6px',
                  boxShadow: '0 2px 8px rgba(95, 165, 231, 0.3)'
                }}
              >
                Nouvel Utilisateur
              </Button>
            </div>
          </div>
        }
      />

      {/* Modal pour ajouter/modifier un utilisateur */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {editingUser ? (
              <>
                <EditOutlined style={{ color: '#5fa5e7', fontSize: '20px' }} />
                <span style={{ fontSize: '18px', fontWeight: '600' }}>Modifier l'Utilisateur</span>
              </>
            ) : (
              <>
                <PlusOutlined style={{ color: '#5fa5e7', fontSize: '20px' }} />
                <span style={{ fontSize: '18px', fontWeight: '600' }}>Nouvel Utilisateur</span>
              </>
            )}
          </div>
        }
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
        style={{ top: 20 }}
        bodyStyle={{ padding: 0 }}
        destroyOnClose
      >
        {errorMessage && (
          <Alert
            message="Erreur"
            description={errorMessage}
            type="error"
            showIcon
            style={{
              margin: '20px 20px 0 20px',
              borderRadius: '6px'
            }}
            closable
            onClose={() => setErrorMessage('')}
          />
        )}
     
        <div style={{ padding: '24px' }}>
          <div style={{
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e8e8e8'
          }}>
            <h2 style={{
              margin: '0',
              fontSize: '16px',
              fontWeight: '600',
              color: '#333'
            }}>
              Informations de l'utilisateur
            </h2>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ marginTop: '8px' }}
          >
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  name="name"
                  label="Nom complet"
                  rules={[
                    { required: true, message: 'Veuillez saisir le nom' },
                    { min: 2, message: 'Minimum 2 caractères' },
                    { max: 100, message: 'Maximum 100 caractères' }
                  ]}
                >
                  <Input
                    placeholder="Ex: John Doe"
                    prefix={<UserOutlined style={{ color: '#999' }} />}
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'Veuillez saisir l\'email' },
                    { type: 'email', message: 'Format email invalide' }
                  ]}
                >
                  <Input
                    placeholder="exemple@email.com"
                    prefix={<MailOutlined style={{ color: '#999' }} />}
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="role"
                  label="Rôle"
                  rules={[{ required: true, message: 'Veuillez sélectionner le rôle' }]}
                >
                  <Select
                    size="large"
                    style={{ borderRadius: '6px' }}
                  >
                    <Option value="User">Utilisateur</Option>
                    <Option value="Admin">Administrateur</Option>
                    <Option value="Moderator">Modérateur</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Divider style={{ margin: '24px 0' }} />

            <div style={{
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e8e8e8'
            }}>
              <h2 style={{
                margin: '0',
                fontSize: '16px',
                fontWeight: '600',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <LockOutlined />
                {editingUser ? 'Changer le mot de passe (optionnel)' : 'Mot de passe'}
              </h2>
            </div>

            {!editingUser ? (
              <>
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      name="password"
                      label="Mot de passe"
                      rules={[{ validator: validatePassword }]}
                    >
                      <Input.Password
                        placeholder="Mot de passe"
                        prefix={<LockOutlined style={{ color: '#999' }} />}
                        size="large"
                        style={{ borderRadius: '6px' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="confirmPassword"
                      label="Confirmer mot de passe"
                      rules={[validateConfirmPassword]}
                    >
                      <Input.Password
                        placeholder="Confirmer le mot de passe"
                        prefix={<LockOutlined style={{ color: '#999' }} />}
                        size="large"
                        style={{ borderRadius: '6px' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ) : (
              <Alert
                message="Information"
                description="Laissez les champs mot de passe vides pour ne pas le modifier."
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: '24px', borderRadius: '6px' }}
              />
            )}

            <Divider style={{ margin: '24px 0' }} />

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '32px',
              paddingTop: '20px',
              borderTop: '1px solid #e8e8e8'
            }}>
              <Button
                onClick={handleCancel}
                disabled={submitting}
                size="large"
                style={{
                  padding: '8px 32px',
                  fontWeight: '500',
                  borderRadius: '6px'
                }}
              >
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
                size="large"
                style={{
                  backgroundColor: '#5fa5e7',
                  borderColor: '#5fa5e7',
                  padding: '8px 32px',
                  fontWeight: '500',
                  borderRadius: '6px'
                }}
              >
                {editingUser ? 'Modifier' : 'Créer'}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      {/* Modal de détail */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <UserOutlined style={{ color: '#5fa5e7', fontSize: '20px' }} />
            <span style={{ fontSize: '18px', fontWeight: '600' }}>Détails de l'Utilisateur</span>
          </div>
        }
        open={detailModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="close" onClick={handleCancel} size="large" style={{ padding: '8px 24px' }}>
            Fermer
          </Button>,
          selectedUser && (
            <Button
              key="edit"
              type="primary"
              onClick={() => {
                handleCancel();
                showModal(selectedUser);
              }}
              size="large"
              style={{
                backgroundColor: '#5fa5e7',
                borderColor: '#5fa5e7',
                padding: '8px 24px'
              }}
              icon={<EditOutlined />}
            >
              Modifier
            </Button>
          )
        ]}
        width={600}
      >
        {selectedUser && (
          <div style={{ padding: '20px 0' }}>
            {/* Header avec avatar */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              padding: '20px',
              backgroundColor: '#f0f7ff',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <Avatar
                size={80}
                icon={<UserOutlined />}
                style={{
                  backgroundColor: '#5fa5e7',
                  fontSize: '32px'
                }}
              />
              <div style={{ flex: 1 }}>
                <h2 style={{
                  margin: '0 0 8px 0',
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  {selectedUser.name}
                </h2>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {(() => {
                    const statutOption = statusOptions.find(s => s.value === selectedUser.status);
                    return (
                      <Tag
                        color={statutOption?.color}
                        style={{
                          padding: '6px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          borderRadius: '20px'
                        }}
                        icon={statutOption?.icon}
                      >
                        {selectedUser.status}
                      </Tag>
                    );
                  })()}
                  <Tag
                    color={selectedUser.role === 'Admin' ? 'blue' : 'green'}
                    style={{
                      padding: '6px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      borderRadius: '20px'
                    }}
                  >
                    {selectedUser.role}
                  </Tag>
                </div>
              </div>
            </div>

            {/* Informations détaillées */}
            <Descriptions
              column={1}
              bordered
              size="middle"
              labelStyle={{
                fontWeight: '600',
                width: '200px',
                backgroundColor: '#fafafa',
                padding: '12px 16px'
              }}
              contentStyle={{
                padding: '12px 16px',
                backgroundColor: 'white'
              }}
              style={{
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              <Descriptions.Item label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MailOutlined />
                  Email
                </div>
              }>
                <div style={{
                  backgroundColor: '#e6f7ff',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1890ff'
                }}>
                  {selectedUser.email}
                </div>
              </Descriptions.Item>
             
              <Descriptions.Item label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarOutlined />
                  Date de création
                </div>
              }>
                <div style={{
                  backgroundColor: '#f6ffed',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {selectedUser.formattedCreatedAt}
                </div>
              </Descriptions.Item>
             
              <Descriptions.Item label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarOutlined />
                  Dernière modification
                </div>
              }>
                <div style={{
                  backgroundColor: '#fff7e6',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {selectedUser.formattedUpdatedAt}
                </div>
              </Descriptions.Item>
             
              <Descriptions.Item label="ID Utilisateur">
                <div style={{
                  backgroundColor: '#f9f0ff',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#722ed1'
                }}>
                  {selectedUser._id || selectedUser.id}
                </div>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>
    </>
  );
};

export default UserManagementPage;
